import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { recordingsApi } from '@api/recordings';
import { HttpError } from '@api/http';
import type { PaginatedResult, Recording, RecordingListQuery } from '@api/types';

interface UseRecordingsState {
  data: PaginatedResult<Recording> | null;
  isLoading: boolean;
  error: string | null;
}

interface UseRecordingsResult extends UseRecordingsState {
  refresh: () => void;
}

export function useRecordings(query: RecordingListQuery): UseRecordingsResult {
  const [state, setState] = useState<UseRecordingsState>({
    data: null,
    isLoading: true,
    error: null,
  });

  const queryKey = JSON.stringify(query);
  const reloadCount = useRef(0);

  const refresh = useCallback(() => {
    reloadCount.current += 1;
    setState((s) => ({ ...s, isLoading: true }));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, isLoading: true, error: null }));

    recordingsApi
      .list(query, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ data, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to load recordings';
        setState((s) => ({ data: s.data, isLoading: false, error: message }));
      });

    return () => controller.abort();
    // queryKey + reloadCount.current intentionally drive refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, reloadCount.current]);

  return { ...state, refresh };
}
